import React, { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { MapPin, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

/** Street-address input with live suggestions (OpenStreetMap + US Census). onPick(s) gets {line1, city, state, zip, county, lat, lng}. */
export function AddressAutocomplete({ value, onChange, onPick, placeholder = "Street address *", testId, context = "" }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const timer = useRef(null);
  const seq = useRef(0);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const search = (q) => {
    clearTimeout(timer.current);
    if (!q || q.trim().length < 4) { setItems([]); return; }
    timer.current = setTimeout(async () => {
      const id = ++seq.current; setLoading(true);
      try {
        const { data } = await api.get("/geocode/suggest", { params: { q: context && !/,/.test(q) ? `${q}, ${context}` : q } });
        if (id === seq.current) { setItems(data); setOpen(true); }
      } catch { /* ignore */ }
      finally { if (id === seq.current) setLoading(false); }
    }, 350);
  };

  return (
    <div className="relative" ref={ref}>
      <Input value={value} placeholder={placeholder} data-testid={testId} autoComplete="off"
        onChange={(e) => { onChange(e.target.value); search(e.target.value); }}
        onFocus={() => items.length && setOpen(true)}
        onKeyDown={(e) => { if (e.key === "Escape" || e.key === "Tab") setOpen(false); }} />
      {loading && <Loader2 className="absolute right-2 top-2.5 h-4 w-4 animate-spin text-slate-400" />}
      {open && items.length > 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg max-h-64 overflow-auto text-sm" data-testid={`${testId}-menu`}>
          {items.map((s, idx) => (
            <button type="button" key={idx} data-testid={`${testId}-option-${idx}`}
              className="w-full text-left px-3 py-2 hover:bg-[#eef5fc] border-b border-slate-100 last:border-0 flex items-start gap-2"
              onMouseDown={(e) => { e.preventDefault(); onPick(s); setOpen(false); setItems([]); }}>
              <MapPin className="h-4 w-4 mt-0.5 text-[#2d7bd0] shrink-0" />
              <div className="min-w-0">
                <div className="font-medium text-slate-900 truncate">{s.line1}</div>
                <div className="text-xs text-slate-500 truncate">{[s.city, s.state].filter(Boolean).join(", ")} {s.zip}{s.precision !== "rooftop" ? " · street only" : ""}</div>
              </div>
            </button>
          ))}
          <div className="px-3 py-1 text-[10px] text-slate-400">Suggestions from OpenStreetMap &amp; US Census. Pick one to pin it on the map exactly.</div>
        </div>
      )}
    </div>
  );
}
